import { formatKodeVerifikasi } from '@satu/shared/verifikasi'
import { CircleAlert, CircleCheck, CircleX } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { KartuSurat } from './format'
import { isi, TEKS } from './strings'

const K = TEKS.periksa.kartu

type Status = KartuSurat['status'] | 'tidakDitemukan'

/**
 * A state is an icon and its word, never colour alone. Ink for ASLI; colour only where the
 * reader must act: ironwood for a replaced letter, the red ochre for a number not found (the
 * DNA spends red on failure).
 */
const STATUS = {
  asli: { Ikon: CircleCheck, warna: 'text-text', kata: K.asli },
  digantikan: { Ikon: CircleAlert, warna: 'text-secondary', kata: K.digantikan },
  tidakDitemukan: { Ikon: CircleX, warna: 'text-primary', kata: K.tidakDitemukan },
} as const satisfies Record<Status, unknown>

interface KartuPeriksaProps {
  /** The number checked, as the field normalised it. */
  kode: string
  /** The letter found, or null for a number SATU does not know. */
  kartu: KartuSurat | null
}

/**
 * The public page's answer (release 15, owner 2026-10-05): the letter's type and the number
 * checked, its state as an icon, a word and one sentence, then the name, the issuing parish and
 * the date on hairlines, label beside value on a phone as on a laptop. Never the book reference.
 * A number not found keeps the number and the state, without facts. Enters with the 200 ms rise.
 */
export function KartuPeriksa({ kode, kartu }: KartuPeriksaProps) {
  const nomor = formatKodeVerifikasi(kode)
  const status: Status = kartu === null ? 'tidakDitemukan' : kartu.status
  const { Ikon, warna, kata } = STATUS[status]
  return (
    <article
      data-kartu-periksa=""
      className="enter flex flex-col gap-4 border border-border p-4 md:p-6"
    >
      {kartu === null ? (
        <h2 className="num font-display text-section">{isi(K.nomorSaja, { kode: nomor })}</h2>
      ) : (
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-section">{kartu.judul}</h2>
          <p className="num text-table text-muted">{isi(K.nomor, { kode: nomor })}</p>
        </div>
      )}
      <div data-status-kartu={status} className="flex items-start gap-3">
        <Ikon aria-hidden="true" className={cn('mt-0.5 size-7 shrink-0', warna)} />
        <div className="flex min-w-0 flex-col gap-1">
          <p data-kata="" className="font-semibold text-section text-text">
            {kata}
          </p>
          <p className="measure font-prose text-prose text-text">
            {kartu === null ? TEKS.kode.tidakDikenal : kartu.kalimat}
          </p>
          {kartu?.terbitanUlang ? (
            <p className="text-table text-muted">{kartu.terbitanUlang}</p>
          ) : null}
        </div>
      </div>
      {kartu === null ? null : (
        <dl className="flex flex-col divide-y divide-hairline border-hairline border-t">
          {kartu.fakta.map((fakta, i) => (
            <div
              key={fakta.label}
              className="grid grid-cols-[8.5rem_minmax(0,1fr)] items-baseline gap-x-4 py-2 md:grid-cols-[10rem_minmax(0,1fr)]"
            >
              <dt className="text-table text-muted">{fakta.label}</dt>
              {/* Two spouses' long names wrap on a phone: a gap keeps each name its own block. */}
              <dd
                className={cn(
                  'flex min-w-0 flex-col gap-2 text-body [overflow-wrap:anywhere]',
                  fakta.redup ? 'text-muted' : 'text-text',
                  i === 0 && !fakta.redup && 'font-medium',
                )}
              >
                {fakta.nilai.map((baris, j) => (
                  // A marriage letter's two spouses may share a name: keyed by place.
                  // biome-ignore lint/suspicious/noArrayIndexKey: the lines never reorder
                  <span key={j} className="block">
                    {baris}
                  </span>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  )
}
